import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, TrashIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Card,
  CardHeader,
  Input,
  Select,
  Badge,
  Modal,
  ModalFooter,
  ContentLoader,
  EmptyState,
} from '@/components/common';
import {
  useChannelTypeGroups,
  useCreateChannelType,
  useDeleteChannelType,
  useChannelPayoutRates,
  useCreateChannelPayoutRate,
  useDeleteChannelPayoutRate,
} from '@/hooks/useChannels';
import { useBrokerInsuranceCompanies } from '@/hooks/useBrokerInsuranceCompanies';
import { useInsurerInsuranceTypes } from '@/hooks/useCommissions';
import type { ChannelType } from '@/services/channels';
import { channelPayoutBasisOptions, basisHidesOdTp, BROKER_COMMISSION_HINT } from './payoutBasis';

const channelTypeEnumOptions = [
  { value: 'POSP', label: 'POSP' },
  { value: 'AGENT', label: 'Agent' },
  { value: 'ADVERTISER', label: 'Advertiser' },
  { value: 'BRANCH', label: 'Branch' },
  { value: 'PARTNER', label: 'Partner' },
  { value: 'OTHER', label: 'Other' },
];

/** Board #252 -- what choosing each base type actually buys, in the user's
 *  terms. A custom type that inherits nothing would be a label with no
 *  commission and no report treatment behind it, so the form says out loud
 *  what it inherits before the type is created. */
const BASE_TYPE_EFFECT: Record<string, string> = {
  POSP: 'Channels of this type provision an agent login and are paid through the POSP waterfall.',
  ADVERTISER:
    "Channels of this type earn a cut of their POSPs' policies and get their own Advertiser report widget.",
  AGENT: 'Channels of this type are paid from this grid and report under Agent.',
  BRANCH: 'Channels of this type are paid from this grid and report under Branch.',
  PARTNER: 'Channels of this type are paid from this grid and report under Partner.',
  OTHER: 'Channels of this type are paid from this grid and report under Other.',
  CONSOLIDATOR:
    'Channels of this type are paid from this grid and get their own Consolidator report widget.',
};

const baseTypeOptions = [
  ...channelTypeEnumOptions.slice(0, 3),
  { value: 'CONSOLIDATOR', label: 'Consolidator' },
  ...channelTypeEnumOptions.slice(3),
];

export function ChannelTypesPage() {
  const { data, isLoading } = useChannelTypeGroups();
  const deleteType = useDeleteChannelType();
  const [showCreate, setShowCreate] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  // Custom types first -- they are what this broker built. Built-in groups
  // follow, carrying the channels that have no custom type: the reporting
  // broker's whole book is that shape, and grouping by custom type alone would
  // have shown them an empty page with 30 agents one click away.
  const groups = [...(data?.custom ?? []), ...(data?.built_in ?? [])];

  return (
    <div>
      <PageHeader
        title="Channel Types"
        subtitle="Define channel types, their payout grids, and see what sits under each"
        breadcrumbs={[{ label: 'Channels', href: '/broker/channels' }, { label: 'Channel Types' }]}
        actions={
          <div className="flex items-center gap-3">
            {/* Item 10: the second "Add Channel" lived here. Two doors to one
                action, and this one sits under a heading about TYPES, which is
                not where a channel is created. Creation stays in the Channels
                section; this links there rather than duplicating it. */}
            <Link to="/broker/channels">
              <Button variant="outline">Go to Channels</Button>
            </Link>
            <Button leftIcon={<PlusIcon className="w-5 h-5" />} onClick={() => setShowCreate(true)}>
              Create Channel Type
            </Button>
          </div>
        }
      />

      {isLoading ? (
        <ContentLoader />
      ) : groups.length === 0 ? (
        <EmptyState
          title="No channel types yet"
          description="Create one to define a payout grid, or add a channel from the Channels section."
        />
      ) : (
        <div className="space-y-4">
          {groups.map((group) => {
            // A built-in group is an ENUM VALUE, not a row this broker created:
            // it cannot be renamed, deleted, or given a payout grid. Keying off
            // `id === null` rather than off the name keeps that honest.
            const isCustom = group.id !== null;
            const key = group.id ?? `built-in:${group.code}`;
            return (
              // `Card` does not spread unknown props, so the test hook lives on a
              // wrapper rather than silently vanishing on the Card.
              <div key={key} data-testid="channel-type-group">
              <Card padding="none">
                <div className="flex items-center justify-between px-5 py-4 gap-3 flex-wrap">
                  <button
                    className="flex items-center gap-3 text-left"
                    onClick={() => setExpanded(expanded === key ? null : key)}
                  >
                    {expanded === key ? (
                      <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                    )}
                    <div>
                      <p className="font-semibold text-gray-900">{group.name}</p>
                      <p className="text-xs text-gray-500 font-mono">{group.code}</p>
                    </div>
                  </button>
                  <div className="flex items-center gap-3 flex-wrap">
                    {isCustom && (
                      <Badge variant="default" size="sm">
                        Behaves as {group.base_type_display || group.base_type}
                      </Badge>
                    )}
                    <Badge variant="info" size="sm">
                      {group.channels.length}{' '}
                      {group.channels.length === 1 ? 'channel' : 'channels'}
                    </Badge>
                    {isCustom && (
                      /* A type with no payout rows earns nothing -- say so
                         rather than showing a neutral "0 payout rates". */
                      <Badge variant={group.rate_count === 0 ? 'warning' : 'default'} size="sm">
                        {group.rate_count === 0
                          ? 'No payout grid — earns nothing'
                          : `${group.rate_count} payout rates`}
                      </Badge>
                    )}
                    {isCustom && (
                      <button
                        className="text-gray-400 hover:text-red-600"
                        title="Delete channel type"
                        onClick={() => {
                          if (confirm(`Delete channel type "${group.name}"?`))
                            deleteType.mutate(group.id!);
                        }}
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="border-t border-gray-100 px-5 py-4">
                  {group.channels.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      No channels under this type yet. Add one from the Channels section.
                    </p>
                  ) : (
                    <ul className="flex flex-wrap gap-2">
                      {group.channels.map((channel) => (
                        <li key={channel.id}>
                          <Link
                            to={channel.detail_path}
                            className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-3 py-1 text-sm hover:bg-gray-50"
                          >
                            <span className="text-gray-900">{channel.name}</span>
                            <span className="text-xs text-gray-400 font-mono">{channel.code}</span>
                            {!channel.is_active && (
                              <Badge variant="warning" size="sm">
                                Inactive
                              </Badge>
                            )}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {expanded === key && isCustom && (
                  <div className="border-t border-gray-100 px-5 py-4 bg-gray-50">
                    <PayoutGrid channelType={group as unknown as ChannelType} />
                  </div>
                )}
              </Card>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && <CreateTypeModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}

function CreateTypeModal({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [baseType, setBaseType] = useState('OTHER');
  const [description, setDescription] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const create = useCreateChannelType();

  const submit = async () => {
    if (!name || !code) return;
    setFieldErrors({});
    try {
      await create.mutateAsync({ name, code, base_type: baseType, description });
      onClose();
    } catch (err) {
      // The server rejects duplicate names/codes per broker with a 400 carrying
      // per-field messages. Surface them ON the fields -- the hook's toast alone
      // leaves the user staring at a form with no idea which input to change.
      setFieldErrors(extractFieldErrors(err));
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Create Channel Type">
      <div className="space-y-4">
        <Input
          label="Name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Bancassurance"
          error={fieldErrors.name}
        />
        <Input
          label="Code"
          required
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\s+/g, '_').toLowerCase())}
          placeholder="e.g. bancassurance"
          error={fieldErrors.code}
        />
        <Select
          label="Behaves as"
          required
          value={baseType}
          onChange={(e) => setBaseType(e.target.value)}
          options={baseTypeOptions}
          error={fieldErrors.base_type}
          helperText={BASE_TYPE_EFFECT[baseType]}
        />
        <Input label="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
        <p className="text-xs text-gray-500">
          A custom type is not a new kind of channel — it inherits the commission
          and reporting behaviour of the built-in type you pick above, and adds
          its own name and payout grid.
        </p>
      </div>
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} isLoading={create.isPending} disabled={!name || !code}>Create</Button>
      </ModalFooter>
    </Modal>
  );
}

/** Pull per-field messages out of this API's error envelope
 *  (`{success:false, error:{fields:{name:[...]}}}`), falling back to DRF's
 *  bare `{name:[...]}` shape. */
function extractFieldErrors(err: unknown): Record<string, string> {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (!data || typeof data !== 'object') return {};
  const envelope = (data as { error?: { fields?: unknown } }).error;
  const raw = (envelope && typeof envelope === 'object' ? envelope.fields : undefined) ?? data;
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (Array.isArray(value) && typeof value[0] === 'string') out[key] = value[0];
    else if (typeof value === 'string') out[key] = value;
  }
  return out;
}

function PayoutGrid({ channelType }: { channelType: ChannelType }) {
  const { data, isLoading } = useChannelPayoutRates(channelType.id);
  const rates = data?.results ?? [];
  const createRate = useCreateChannelPayoutRate();
  const deleteRate = useDeleteChannelPayoutRate();

  const { data: insurers } = useBrokerInsuranceCompanies();
  const [insurer, setInsurer] = useState('');
  const [insuranceType, setInsuranceType] = useState('');
  const [percentage, setPercentage] = useState('');
  const [premiumBase, setPremiumBase] = useState('net_premium');
  const { data: insurerTypes } = useInsurerInsuranceTypes(insurer || undefined);

  const addRate = async () => {
    if (!percentage) return;
    await createRate.mutateAsync({
      channel_type: channelType.id,
      insurer: insurer || null,
      insurance_type: insuranceType || null,
      commission_percentage: percentage,
      premium_base: premiumBase,
    });
    setPercentage('');
    setInsuranceType('');
  };

  return (
    <div className="space-y-4">
      <CardHeader title="Payout Grid" subtitle="Commission this channel type earns" />

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : rates.length === 0 ? (
        <p className="text-sm text-gray-500">No payout rates yet — add one below.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Insurer</th>
                <th className="px-4 py-2 text-left font-medium">Insurance Type</th>
                <th className="px-4 py-2 text-left font-medium">Percentage</th>
                <th className="px-4 py-2 text-left font-medium">Commission On</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rates.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-2 text-gray-700">{r.insurer_name || 'All insurers'}</td>
                  <td className="px-4 py-2 text-gray-700">{r.insurance_type_name || 'All types'}</td>
                  <td className="px-4 py-2 font-medium text-gray-900">{r.commission_percentage}%</td>
                  <td className="px-4 py-2 text-gray-700">
                    {channelPayoutBasisOptions.find((o) => o.value === r.premium_base)?.label || r.premium_base}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      className="text-gray-400 hover:text-red-600"
                      onClick={() => deleteRate.mutate(r.id)}
                      title="Remove"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
        <Select
          label="Insurer (optional)"
          value={insurer}
          onChange={(e) => {
            setInsurer(e.target.value);
            setInsuranceType('');
          }}
          options={(insurers || []).map((i) => ({ value: i.id, label: i.name }))}
          placeholder="All insurers"
        />
        <Select
          label="Insurance Type (optional)"
          value={insuranceType}
          onChange={(e) => setInsuranceType(e.target.value)}
          options={(insurerTypes || []).map((t) => ({ value: t.id, label: t.name }))}
          placeholder="All types"
        />
        <Input
          label="Percentage"
          type="number"
          step="0.01"
          value={percentage}
          onChange={(e) => setPercentage(e.target.value)}
        />
        <div>
          <Select
            label="Commission On"
            value={premiumBase}
            onChange={(e) => setPremiumBase(e.target.value)}
            options={channelPayoutBasisOptions}
          />
          {basisHidesOdTp(premiumBase) && (
            <p className="text-xs text-blue-600 mt-1">{BROKER_COMMISSION_HINT}</p>
          )}
        </div>
        <Button onClick={addRate} isLoading={createRate.isPending} disabled={!percentage}>
          Add Rate
        </Button>
      </div>
    </div>
  );
}
